import { Client, Databases, Query, Account, ID } from "appwrite";
import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  const isLiteracy = searchParams.get("literacy") === "true";
  const customerId = searchParams.get("customer_id");
  
  const projectId = isLiteracy ? "ai-literacy" : "aireadinessdev";
  const databaseId = isLiteracy ? "ai-literacy" : "aireadinessdb";
  
  // Create Appwrite client
  const client = useMemo(() => new Client(), []);
  client
    .setEndpoint("https://api.randomwalk.ai/v1")
    .setProject(projectId);
  
  // Create Databases instance
  const databases = useMemo(() => new Databases(client), [client]);
  
  // Function to download the receipt from Appwrite
  const downloadReceipt = async () => {
    setIsLoading(true);
    setErrorMessage("");
    
    try {
      // Check if customerId exists
      if (!customerId) {
        throw new Error("Customer ID is missing");
      }
      
      console.log("Fetching receipt for customer:", customerId);
      
      const response = await databases.listDocuments(
        databaseId,
        "test",
        [Query.equal("c_id", customerId)]
      );
      
      console.log("Response from database:", response);
      
      if (!response.documents || response.documents.length === 0) {
        throw new Error("No receipt found for this customer");
      }
      
      const receiptDoc = response.documents[0];
      console.log("Receipt document:", receiptDoc);
      
      // Check if receipt_url exists in the document
      if (!receiptDoc.receipt_url) {
        throw new Error("Receipt URL not found in the document");
      }
      
      const receiptUrl = receiptDoc.receipt_url;
      console.log("Receipt URL:", receiptUrl);
      
      // Directly handle opening the URL
      handleOpenReceipt(receiptUrl);
      
      return receiptUrl;
    } catch (error) {
      console.error("Error downloading receipt:", error);
      setErrorMessage(error.message || "Failed to download receipt");
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Separate function to handle opening the URL
  const handleOpenReceipt = (url) => {
    try {
      // First approach: Use window.location for direct navigation
      // This is more reliable on mobile devices
      if (!url.startsWith("http")) {
        url = "https://" + url;
      }
      
      // Try to open in new tab
      const newWindow = window.open(url, '_blank');
      
      // If blocked by popup blocker or failed
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        console.log("Popup blocked, trying direct navigation");
        window.location.href = url;
      }
    } catch (e) {
      console.error("Error opening receipt:", e);
      // Fallback - offer URL for manual copy
      alert("Please copy this URL to view your receipt: " + url);
    }
  };
  
  return (
    <div className="flex items-center justify-center h-screen bg-gray-100 font-display">
      <div className="bg-white p-8 md:p-12 rounded-xl shadow-xl text-center relative m-4 md:m-0 max-w-lg">
        <div id="tick-symbol" className="flex items-center justify-center w-16 h-16 bg-[#27D152] mx-auto rounded-full hover:scale-110 transition-transform duration-200 ease-in-out">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="#FFFFFF"
            className="w-8 h-8"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="5"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h1 className="mt-6 text-2xl font-bold text-gray-900">Payment Successful!</h1>
        <p className="mt-2 text-lg font-medium text-gray-700">
          Thank you for your purchase. Your payment has been processed.
        </p>

        <div className="mt-8 space-y-4">
          {/* Download Invoice Button with loading state */}
          <button
            id="download-receipt"
            className={`w-3/4 mb-4 ${isLoading ? 'bg-blue-300' : 'bg-blue-500 hover:bg-blue-700'} text-white font-semibold py-3 rounded-lg transition-colors duration-200`}
            onClick={downloadReceipt}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : "View Receipt"}
          </button>
          
          {/* Error message display */}
          {errorMessage && (
            <p className="text-red-500 text-sm">{errorMessage}</p>
          )}

          {isLiteracy ? (
            <p className="mt-4 text-sm text-gray-600">
              A link to your <strong>AI Literacy Assessment</strong> has been sent to your registered email.
            </p>
          ) : (
            <p className="mt-4 text-sm text-gray-600">
              A link to your <strong>AI Readiness Assessment</strong> has been sent to your registered email.
            </p>
          )}
          {isLiteracy ? (
            <p className="text-sm text-gray-600 mt-4">
              Since you selected the AI Literacy version, your access is limited.{" "}
              <button id="redirecting-to-test" className="text-green-600 font-bold text-lg" onClick={() => window.open("https://ai-literacy.randomwalk.ai/", "_blank")}>
                Click here
              </button>{" "}
              to begin your literacy journey.
            </p>
          ) : (
            <p className="text-sm text-gray-600 mt-4">
              Please check your email for the link to your AI Readiness Assessment. 
            </p>
          )}

        </div>
      </div>
    </div>
  );
};

export default SuccessPage;